import * as React from 'react';

import styled from '@emotion/styled';
import { Box, Flex } from '@lightspeed/flame/Core';

import AlertMessageDisplay, {
  AlertMessageDTO,
} from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import { ConfigContext } from 'root/apps/contexts/ConfigContext';
import { AlertMessageContext } from 'root/apps/InventoryCountReconcile/contexts/AlertMessage';
import { ReconcileDataContext } from 'root/apps/InventoryCountReconcile/contexts/ReconcileData';
import { InventoryCountDTO, ReconcileStatus } from 'root/apps/InventoryCountReconcile/types';
import { GuiDefGroup } from 'root/apps/InventoryCountReconcile/views/components/GuiDefGroup';
import IntroView from 'root/apps/InventoryCountReconcile/views/components/IntroView';
import LoadingBones from 'root/apps/InventoryCountReconcile/views/components/LoadingBones';
import ProgressView from 'root/apps/InventoryCountReconcile/views/components/ProgressView';

export const StyledBox = styled(Box)`
  max-width: 50%;
  @media screen and (max-width: 1260px) {
    max-width: 80%;
  }
`;

const InventoryCountReconcileView: React.FC = () => {
  const { isLoading, data, getReconcileData, closeReconcile } =
    React.useContext(ReconcileDataContext);
  const { alertMessage, setAlertMessage } = React.useContext(AlertMessageContext);
  const { getConfig } = React.useContext(ConfigContext);
  const [activeReconcile, setActiveReconcile] = React.useState(false);

  React.useEffect(() => {
    getReconcileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Trigger once a listing refresh after active reconcile becomes idle
   */
  const activeStatuses: ReconcileStatus[] = ['reconciliation_queued', 'reconciliation_running'];
  const idleStatuses: ReconcileStatus[] = [
    'awaiting_reconciliation',
    'reconciliation_done',
    'reconciliation_failed',
    'reconciliation_success',
  ];
  React.useEffect(() => {
    if (!data || !data.reconcileStatus) {
      return;
    }
    if (activeStatuses.includes(data.reconcileStatus)) {
      if (!activeReconcile) {
        setActiveReconcile(true);
      }
      return;
    }
    if (idleStatuses.includes(data.reconcileStatus) && activeReconcile) {
      setActiveReconcile(false);
      const refreshListingID = getConfig('refreshListingID');
      if (refreshListingID) {
        window.RefreshListing(refreshListingID);
      }
    }
  }, [data, activeReconcile, activeStatuses, idleStatuses, setActiveReconcile, getConfig]);

  /**
   * Status to View mapping
   */
  const statusCheck = (currentStatus: ReconcileStatus | undefined, list: string[]) => {
    if (currentStatus) {
      return list.includes(currentStatus);
    }
    return false;
  };
  const introView: ReconcileStatus[] = [
    'awaiting_reconciliation',
    'reconciliation_failed',
    'reconciliation_success',
    'reconciliation_done',
  ];
  const progressView: ReconcileStatus[] = ['reconciliation_queued', 'reconciliation_running'];

  /**
   * Status to AlertMessageDisplay mapping
   */
  const AlertMessageDisplayFromInventoryCountDTO = (data: InventoryCountDTO | undefined) => {
    let alertMessage: AlertMessageDTO | undefined;
    // eslint-disable-next-line default-case
    switch (data?.reconcileStatus) {
      case 'reconciliation_success':
        alertMessage = {
          message: 'You can find inventory discrepancies in the Shrinkage tab.',
          title:
            data.totalSuccess > 1
              ? `${data.totalSuccess} items reconciled`
              : `${data.totalSuccess} item reconciled`,
          type: 'success',
        };
        break;
      case 'reconciliation_failed':
        alertMessage = {
          message: 'There was an error. Try reconciling again.',
          title:
            data.totalErrors > 1
              ? `${data.totalErrors} items didn't reconcile`
              : `${data.totalErrors} item didn't reconcile`,
          type: 'danger',
        };
    }
    if (alertMessage) {
      return (
        <AlertMessageDisplay
          alertMessage={alertMessage}
          onClose={handleAlertMessageClose}
          key="status"
          mb={2}
        />
      );
    }
    return null;
  };

  const handleAlertMessageClose = (e: React.MouseEvent) => {
    e.preventDefault();
    // not awaiting - fire & forget
    closeReconcile();
  };

  const handleDismissError = (e: React.MouseEvent) => {
    e.preventDefault();
    setAlertMessage(null);
  };

  return (
    <Box>
      {alertMessage ? (
        <AlertMessageDisplay
          alertMessage={alertMessage}
          onClose={handleDismissError}
          key="error"
          mb={2}
        />
      ) : (
        AlertMessageDisplayFromInventoryCountDTO(data)
      )}
      <StyledBox>
        <GuiDefGroup title="Reconcile inventory count">
          <Flex flexDirection="column" justifyContent="space-between" height="100%" minHeight={30}>
            {isLoading && !data ? <LoadingBones /> : null}
            {statusCheck(data?.reconcileStatus, introView) ? <IntroView /> : null}
            {statusCheck(data?.reconcileStatus, progressView) ? <ProgressView /> : null}
          </Flex>
        </GuiDefGroup>
      </StyledBox>
    </Box>
  );
};

export default InventoryCountReconcileView;
